import React from "react";
import {
  Stack,
  Checkbox,
  Tag,
  TagLabel,
  Flex,
  Text,
  Card,
} from "@chakra-ui/react";
import { appRoutes } from "../../../../../App";
import { t } from "i18next";
import { ResourceSelector } from "./ResourceSelector";
import { compressUUID } from "../../../../shared/utils/format-utils";
import { ApiClient, ResourceType } from "../../../models/ApiClient";
import { ExternalLinkIcon } from "../../../../shared/views/icons";

const i18prefix = "account:Integrations.permissions";

interface Props {
  apiClient: ApiClient;
  permissions: Map<string, ResourceType>;
  setPermissions: React.Dispatch<
    React.SetStateAction<Map<string, ResourceType>>
  >;
}

export const ManagePermissions: React.FC<Props> = ({
  apiClient,
  permissions,
  setPermissions,
}) => {
  return (
    <Card.Root variant="outline" p={3}>
      <Stack>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase">
          {t(`${i18prefix}.title`)}
        </Text>

        <Flex flexWrap="wrap" gap={2}>
          {[...permissions].map(([entity, resource], i) => (
            <Tag.Root key={i}>
              {resource !== ResourceType.Any && (
                <Tag.StartElement>
                  <ExternalLinkIcon
                    onClick={() => {
                      const url =
                        resource === ResourceType.Project
                          ? appRoutes.project(entity)
                          : appRoutes.osaWay(entity);
                      window.open(url, "_blank")?.focus();
                    }}
                  />
                </Tag.StartElement>
              )}
              <TagLabel
                mt={1}
                fontFamily="monospace"
                textDecoration={
                  permissions.has("*") && entity !== "*"
                    ? "line-through"
                    : undefined
                }
              >
                {`${resource}/${compressUUID(entity)}`}
              </TagLabel>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  onClick={() => {
                    setPermissions((prev) => {
                      prev.delete(entity);
                      return new Map(prev);
                    });
                  }}
                />
              </Tag.EndElement>
            </Tag.Root>
          ))}
        </Flex>

        <Checkbox.Root
          size="sm"
          colorPalette="primary"
          checked={permissions.has("*")}
          onCheckedChange={({ checked }) => {
            setPermissions((prev) => {
              if (checked) {
                prev.set("*", ResourceType.Any);
              } else {
                prev.delete("*");
              }
              return new Map(prev);
            });
          }}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>{t(`${i18prefix}.allowAll`)}</Checkbox.Label>
        </Checkbox.Root>

        <ResourceSelector
          apiClient={apiClient}
          permissions={permissions}
          setPermissions={setPermissions}
        />
      </Stack>
    </Card.Root>
  );
};
